import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Users, UserCheck, BookOpen, CalendarCheck,
  ClipboardList, DollarSign, Bell, Library, Bus, BarChart2,
  ChevronDown, ChevronRight, LogOut, X, BookMarked, UserPlus, Settings
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const navConfig = [
  { label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard', roles: ['admin','teacher','student','parent'] },
  {
    label: 'Enquiry', icon: UserPlus, roles: ['admin'],
    children: [
      { label: 'All Enquiries', path: '/admission/enquiries', roles: ['admin'] },
      { label: 'New Enquiry', path: '/admission/enquiry', roles: ['admin'] },
    ]
  },
  {
    label: 'Homework', icon: BookMarked, roles: ['admin','teacher','parent','student'],
    children: [
      { label: 'Homework', path: '/homework', roles: ['admin','teacher','parent','student'] },
      { label: 'Add Homework', path: '/homework/add', roles: ['admin','teacher'] },
      { label: 'Reports', path: '/homework/reports', roles: ['admin','teacher'] },
    ]
  },
  {
    label: 'Students', icon: Users, roles: ['admin','teacher'],
    children: [
      { label: 'All Students', path: '/students', roles: ['admin','teacher'] },
      { label: 'New Admission', path: '/students/admission', roles: ['admin'] },
      { label: 'Class Allocation', path: '/students/allocation', roles: ['admin'] },
    ]
  },
  {
    label: 'Staff', icon: UserCheck, roles: ['admin'],
    children: [
      { label: 'All Staff', path: '/staff', roles: ['admin'] },
      { label: 'Register Staff', path: '/staff/register', roles: ['admin'] },
    ]
  },
  {
    label: 'Academic', icon: BookOpen, roles: ['admin','teacher'],
    children: [
      { label: 'Classes', path: '/academic/classes', roles: ['admin'] },
      { label: 'Subjects', path: '/academic/subjects', roles: ['admin'] },
      { label: 'Timetable', path: '/academic/timetable', roles: ['admin','teacher'] },
      { label: 'Lesson Plans', path: '/academic/lesson-plans', roles: ['admin','teacher'] },
    ]
  },
  {
    label: 'Attendance', icon: CalendarCheck, roles: ['admin','teacher'],
    children: [
      { label: 'Student Attendance', path: '/attendance/students', roles: ['admin','teacher'] },
      { label: 'Staff Attendance', path: '/attendance/staff', roles: ['admin'] },
      { label: 'Reports', path: '/attendance/reports', roles: ['admin','teacher'] },
    ]
  },
  {
    label: 'Examinations', icon: ClipboardList, roles: ['admin','teacher','student','parent'],
    children: [
      { label: 'Exam Schedule', path: '/exams/schedule', roles: ['admin','teacher','student','parent'] },
      { label: 'Marks Entry', path: '/exams/marks', roles: ['admin','teacher'] },
      { label: 'Results', path: '/exams/results', roles: ['admin','teacher','student','parent'] },
      { label: 'Hall Ticket', path: '/exams/hall-ticket', roles: ['admin','student','parent'] },
    ]
  },
  {
    label: 'Fees', icon: DollarSign, roles: ['admin','parent','student'],
    children: [
      { label: 'Fee Structure', path: '/fees/structure', roles: ['admin'] },
      { label: 'Collect Fee', path: '/fees/collection', roles: ['admin'] },
      { label: 'Receipts', path: '/fees/receipts', roles: ['admin','parent','student'] },
      { label: 'Due Tracking', path: '/fees/dues', roles: ['admin'] },
    ]
  },
  {
    label: 'Communication', icon: Bell, roles: ['admin','teacher','parent','student'],
    children: [
      { label: 'Announcements', path: '/communication/announcements', roles: ['admin','teacher','parent','student'] },
    ]
  },
  {
    label: 'Library', icon: Library, roles: ['admin','teacher','student'],
    children: [
      { label: 'Book Catalog', path: '/library/catalog', roles: ['admin','teacher','student'] },
      { label: 'Issue / Return', path: '/library/issue-return', roles: ['admin'] },
    ]
  },
  {
    label: 'Transport', icon: Bus, roles: ['admin','parent','student'],
    children: [
      { label: 'Routes', path: '/transport/routes', roles: ['admin'] },
      { label: 'Vehicles', path: '/transport/vehicles', roles: ['admin'] },
    ]
  },
  {
    label: 'Settings', icon: Settings, roles: ['admin'],
    children: [
      { label: 'User Management', path: '/settings/users', roles: ['admin'] },
    ]
  },
  {
    label: 'Reports', icon: BarChart2, roles: ['admin'],
    children: [
      { label: 'Overview', path: '/reports', roles: ['admin'] },
      { label: 'Attendance Summary', path: '/reports/attendance', roles: ['admin'] },
      { label: 'Financial Report', path: '/reports/financial', roles: ['admin'] },
    ]
  },
];

function NavItem({ item, role }) {
  const [open, setOpen] = useState(false);
  if (!item.roles.includes(role)) return null;
  if (item.children) {
    const visibleChildren = item.children.filter(c => c.roles.includes(role));
    if (visibleChildren.length === 0) return null;
    return (
      <div>
        <button onClick={() => setOpen(o => !o)}
          className="w-full flex items-center gap-2 px-2 py-1 rounded-lg text-sm hover:bg-gray-100 transition-colors" style={{ color: '#000' }}>
          <item.icon size={16} className="flex-shrink-0" />
          <span className={`flex-1 text-left ${open ? 'font-semibold' : 'font-normal'}`}>{item.label}</span>
          {open ? <ChevronDown size={13} className="text-gray-500" /> : <ChevronRight size={13} className="text-gray-500" />}
        </button>
        {open && (
          <div className="ml-6 mt-0.5 space-y-0.5">
            {visibleChildren.map(child => (
              <NavLink key={child.path} to={child.path}
                style={({ isActive }) => ({ color: isActive ? undefined : '#333' })}
                className={({ isActive }) => `flex items-center gap-1.5 px-3 py-1 rounded-lg text-sm transition-colors ${isActive ? 'bg-green-500 text-white font-semibold' : 'hover:bg-gray-100'}`}>
                <span className="">›</span>
                {child.label}
              </NavLink>
            ))}
          </div>
        )}
      </div>
    );
  }
  return (
    <NavLink to={item.path}
      style={({ isActive }) => ({ color: isActive ? undefined : '#000' })}
      className={({ isActive }) => `flex items-center gap-2 px-2 py-1 rounded-lg text-sm transition-colors ${isActive ? 'bg-green-500 text-white font-semibold' : 'hover:bg-gray-100'}`}>
      <item.icon size={16} className="flex-shrink-0" />
      <span className="font-normal">{item.label}</span>
    </NavLink>
  );
}

function Sidebar({ mobile, onClose }) {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <div className={`${mobile ? 'fixed inset-y-0 left-0 z-50 w-64' : 'relative w-full'} flex flex-col h-full bg-white border-r border-gray-200`}>
      {/* Logo / School name */}
      <div className="flex items-center justify-between border-b border-gray-200 h-16 flex-shrink-0">
        <div className="flex-1 h-full flex items-center justify-center">
          <img src="/school-logo.png" alt="logo" style={{ height: '3rem' }} className="object-contain"
            onError={e => e.target.parentElement.style.display = 'none'} />
        </div>
        {mobile && <button onClick={onClose} className="text-gray-400 hover:text-gray-700 px-3"><X size={20} /></button>}
      </div>

      <nav className="flex-1 overflow-y-auto sidebar-scroll p-2 space-y-0">
        {navConfig.map(item => <NavItem key={item.label} item={item} role={currentUser?.role} />)}
      </nav>

      <div className="p-3 border-t border-gray-200">
        <div className="flex items-center gap-2.5 px-2 mb-2">
          <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold"
            style={{ backgroundColor: '#16a34a' }}>
            {currentUser?.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
          </div>
          <div className="min-w-0">
            <p className="text-gray-800 text-sm font-medium truncate">{currentUser?.name}</p>
            <p className="text-gray-400 capitalize">{currentUser?.role}</p>
          </div>
        </div>
        <button onClick={handleLogout} className="w-full flex items-center gap-2 px-3 py-1 rounded-lg text-sm hover:bg-gray-100 transition-colors">
          <LogOut size={16} /><span>Logout</span>
        </button>
      </div>
    </div>
  );
}
export default Sidebar;
